"use client";

import {useMemo, useState, useTransition} from "react";
import {useRouter} from "next/navigation";
import {formatBlackCardLine} from "@/lib/customPacks";

type CustomPack = {
    id: string;
    name: string;
    cardCountBlack: number;
    cardCountWhite: number;
    blackCards: Array<{text: string; pick: number}>;
    whiteCards: string[];
};

function countLines(value: string) {
    return value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .length;
}

export function CustomPackManager({packs}: {packs: CustomPack[]}) {
    const router = useRouter();
    const [editingPackId, setEditingPackId] = useState<string | null>(null);
    const [name, setName] = useState("");
    const [blackCardsText, setBlackCardsText] = useState("");
    const [whiteCardsText, setWhiteCardsText] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [isPending, startTransition] = useTransition();

    const editingPack = packs.find((pack) => pack.id === editingPackId) ?? null;
    const blackCount = useMemo(() => countLines(blackCardsText), [blackCardsText]);
    const whiteCount = useMemo(() => countLines(whiteCardsText), [whiteCardsText]);

    function beginEdit(pack: CustomPack) {
        setEditingPackId(pack.id);
        setName(pack.name);
        setBlackCardsText(pack.blackCards.map((card) => formatBlackCardLine(card.text, card.pick)).join("\n"));
        setWhiteCardsText(pack.whiteCards.join("\n"));
        setError(null);
        setMessage(null);
    }

    function cancelEdit() {
        setEditingPackId(null);
        setName("");
        setBlackCardsText("");
        setWhiteCardsText("");
        setError(null);
        setMessage(null);
    }

    return (
        <section className="panel pack-library-section">
            <div className="section-heading">
                <div>
                    <span className="section-kicker">Your Library</span>
                    <h2>Private custom packs</h2>
                </div>
            </div>
            <div className="pack-grid">
                {packs.length > 0 ? packs.map((pack) => (
                    <article className="pack-card static custom-pack-card" key={pack.id}>
                        <div className="pack-card-top">
                            <span className="pack-count">{pack.cardCountBlack} black / {pack.cardCountWhite} white</span>
                            <span className="pill private">Private</span>
                        </div>
                        <strong>{pack.name}</strong>
                        <div className="inline-actions pack-card-actions">
                            <button className="button button-ghost" onClick={() => beginEdit(pack)} type="button">Edit</button>
                            <button
                                className="button button-secondary"
                                disabled={isPending}
                                onClick={() => {
                                    startTransition(async () => {
                                        setError(null);
                                        setMessage(null);
                                        try {
                                            const response = await fetch(`/api/packs/custom/${pack.id}`, {method: "DELETE"});
                                            const result = await response.json() as {error?: string};
                                            if (!response.ok) {
                                                throw new Error(result.error ?? "Failed to delete pack");
                                            }
                                            if (editingPackId === pack.id) {
                                                cancelEdit();
                                            }
                                            setMessage(`Deleted "${pack.name}".`);
                                            router.refresh();
                                        } catch (cause) {
                                            console.error("Failed to delete custom pack.", cause);
                                            setError(cause instanceof Error ? cause.message : "Failed to delete custom pack");
                                        }
                                    });
                                }}
                                type="button"
                            >
                                Delete
                            </button>
                        </div>
                    </article>
                )) : <div className="empty-state">No private packs yet. Build one above, then mix it into a lobby.</div>}
            </div>
            {editingPack ? (
                <section className="panel custom-pack-panel nested">
                    <div className="section-heading">
                        <div>
                            <span className="section-kicker">Editing</span>
                            <h2>{editingPack.name}</h2>
                        </div>
                        <div className="pack-builder-stats">
                            <span className="muted">{blackCount} black / {whiteCount} white</span>
                        </div>
                    </div>
                    <label className="pack-builder-field">
                        <span className="card-eyebrow">Pack Name</span>
                        <input className="join-input pack-builder-input" maxLength={80} onChange={(event) => setName(event.target.value)} value={name} />
                    </label>
                    <div className="pack-builder-grid">
                        <label className="pack-builder-field">
                            <span className="card-eyebrow">Black Cards</span>
                            <textarea className="json-input compact" onChange={(event) => setBlackCardsText(event.target.value)} spellCheck={false} value={blackCardsText} />
                        </label>
                        <label className="pack-builder-field">
                            <span className="card-eyebrow">White Cards</span>
                            <textarea className="json-input compact" onChange={(event) => setWhiteCardsText(event.target.value)} spellCheck={false} value={whiteCardsText} />
                        </label>
                    </div>
                    <div className="setup-footer">
                        <button className="button button-ghost" onClick={cancelEdit} type="button">Cancel</button>
                        <button
                            className="button button-primary"
                            disabled={isPending || !name.trim() || blackCount + whiteCount === 0}
                            onClick={() => {
                                startTransition(async () => {
                                    setError(null);
                                    setMessage(null);
                                    try {
                                        const response = await fetch(`/api/packs/custom/${editingPack.id}`, {
                                            method: "PUT",
                                            headers: {"content-type": "application/json"},
                                            body: JSON.stringify({name, blackCardsText, whiteCardsText})
                                        });
                                        const result = await response.json() as {error?: string; pack?: {name: string}};
                                        if (!response.ok) {
                                            throw new Error(result.error ?? "Failed to update pack");
                                        }
                                        setMessage(`Saved "${result.pack?.name ?? name}".`);
                                        router.refresh();
                                    } catch (cause) {
                                        console.error("Failed to update custom pack.", cause);
                                        setError(cause instanceof Error ? cause.message : "Failed to update custom pack");
                                    }
                                });
                            }}
                            type="button"
                        >
                            {isPending ? "Saving..." : "Save changes"}
                        </button>
                    </div>
                </section>
            ) : null}
            {message ? <p className="inline-note">{message}</p> : null}
            {error ? <p className="error-text">{error}</p> : null}
        </section>
    );
}
